/**
 * User Endpoint Selections API service
 *
 * Provides functions for managing user endpoint selections within cases.
 */

import { apiClient } from './api'
import type {
  UserSelection,
  SetSelectionRequest,
  ModifySelectionRequest,
  SelectFromFilterRequest,
  FilteredCountResponse,
} from '../types/case-container'

// =============================================================================
// SELECTION API
// =============================================================================

/**
 * Get current selection for the authenticated user
 */
export async function getSelection(caseId: string): Promise<UserSelection> {
  return apiClient.get<UserSelection>(`/cases/${caseId}/selections`)
}

/**
 * Replace entire selection set
 */
export async function setSelection(
  caseId: string,
  request: SetSelectionRequest
): Promise<UserSelection> {
  return apiClient.put<UserSelection>(`/cases/${caseId}/selections`, request)
}

/**
 * Add endpoint IDs to selection
 */
export async function addToSelection(
  caseId: string,
  request: ModifySelectionRequest
): Promise<UserSelection> {
  return apiClient.patch<UserSelection>(`/cases/${caseId}/selections/add`, request)
}

/**
 * Remove endpoint IDs from selection
 */
export async function removeFromSelection(
  caseId: string,
  request: ModifySelectionRequest
): Promise<UserSelection> {
  return apiClient.patch<UserSelection>(`/cases/${caseId}/selections/remove`, request)
}

/**
 * Clear all selections
 */
export async function clearSelection(caseId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/selections`)
}

/**
 * Select all items matching filter criteria
 */
export async function selectFromFilter(
  caseId: string,
  request: SelectFromFilterRequest
): Promise<UserSelection> {
  return apiClient.post<UserSelection>(`/cases/${caseId}/selections/from-filter`, request)
}

/**
 * Get count of items matching filter (for preview)
 */
export async function getFilteredCount(
  caseId: string,
  request: SelectFromFilterRequest
): Promise<FilteredCountResponse> {
  return apiClient.post<FilteredCountResponse>(`/cases/${caseId}/selections/filter-count`, request)
}
